import { useState } from 'react'
import { MessageSquareText } from 'lucide-react'
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import { usePropertyKnowledgeThread } from '@/hooks/useApi'
import { formatLocaleDateTime } from '@/lib/format-utils'

/** A single citation attached to an Ask answer, as returned by the API. */
export interface AskSource {
  sourceType: string
  sourceRef?: string | null
  refId?: string | null
  occurredAt?: string | null
}

interface KnowledgeThreadMessage {
  sender?: string | null
  sentAt?: string | null
  content?: string | null
}

// Human-readable label per source_type. Falls back to the raw value for any
// type not yet known here (e.g. a new source added on the backend before the
// UI catches up), so a citation never silently disappears.
const SOURCE_TYPE_LABELS: Record<string, string> = {
  messages: 'guest messages',
}

function formatSourceLabel(source: AskSource) {
  const typeLabel = SOURCE_TYPE_LABELS[source.sourceType] || source.sourceType
  return source.sourceRef ? `${typeLabel} — ${source.sourceRef}` : typeLabel
}

/**
 * Collapses a message's sources down to one citation row per distinct
 * conversation/source. When refId is present, that alone is the identity —
 * two chunks citing the same thread can carry different occurredAt values
 * (different excerpts from the same conversation), and including occurredAt
 * in the key would wrongly render them as separate rows with duplicate
 * "View conversation" links to the same thread. Falls back to the coarser
 * (sourceType, sourceRef, occurredAt) composite only for sources with no
 * thread id to dedupe by.
 */
export function dedupeSources(sources: AskSource[]) {
  const seen = new Set<string>()
  const result: AskSource[] = []
  for (const source of sources) {
    const key = source.refId
      ? JSON.stringify([source.sourceType, source.refId])
      : JSON.stringify([source.sourceType, source.sourceRef, source.occurredAt])
    if (seen.has(key)) continue
    seen.add(key)
    result.push(source)
  }
  return result
}

interface ConversationSnapshotProps {
  propertyId: string
  threadId: string
  sourceType: string
  open: boolean
}

function ConversationSnapshot({
  propertyId,
  threadId,
  sourceType,
  open,
}: ConversationSnapshotProps) {
  const { data, isLoading, error, refetch } = usePropertyKnowledgeThread(
    propertyId,
    threadId,
    sourceType,
    { enabled: open }
  )

  if (isLoading) {
    return (
      <div className="space-y-2">
        <LoadingSkeleton className="h-12 w-full" />
        <LoadingSkeleton className="h-12 w-full" />
      </div>
    )
  }
  if (error) {
    return <ErrorState error={error} onRetry={refetch} />
  }
  // `messages` is contractually `unknown` on GetPropertyKnowledgeThreadResponse
  // (deliberately unmodeled arbitrary JSON) - narrow it here to the shape this
  // component actually reads.
  const messages = (data?.messages ?? []) as KnowledgeThreadMessage[]
  return (
    <div className="max-h-[60vh] space-y-2 overflow-y-auto">
      {messages.map((message, index) => (
        <div key={index} className="rounded-card border border-border bg-surface2 p-2">
          <div className="flex items-center justify-between gap-2 text-xs text-textMuted">
            <span className="font-medium text-text">{message.sender || 'Unknown'}</span>
            {message.sentAt && (
              <span>{formatLocaleDateTime(message.sentAt, { fallback: null })}</span>
            )}
          </div>
          <p className="mt-1 whitespace-pre-wrap wrap-break-word text-sm text-text">
            {message.content}
          </p>
        </div>
      ))}
    </div>
  )
}

interface SourceCitationRowProps {
  propertyId: string
  source: AskSource
}

function SourceCitationRow({ propertyId, source }: SourceCitationRowProps) {
  const [open, setOpen] = useState(false)
  // Only fetched to populate the dialog's title (guest name) — the message
  // list itself is re-fetched/rendered by ConversationSnapshot below, but
  // both share the same query key/cache so this doesn't double the request.
  const { data } = usePropertyKnowledgeThread(propertyId, source.refId, source.sourceType, {
    enabled: open,
  })
  const dateLabel = formatLocaleDateTime(source.occurredAt, { fallback: null })

  return (
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-textMuted">
      <span>
        Found in {formatSourceLabel(source)}
        {dateLabel ? ` — ${dateLabel}` : ''}
      </span>
      {source.refId && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1 text-mint hover:underline"
            >
              <MessageSquareText className="h-3 w-3" />
              View conversation
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{data?.guestName || 'Guest conversation'}</DialogTitle>
              {dateLabel && <DialogDescription>{dateLabel}</DialogDescription>}
            </DialogHeader>
            <ConversationSnapshot
              propertyId={propertyId}
              threadId={source.refId}
              sourceType={source.sourceType}
              open={open}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

interface AskSourceCitationsProps {
  propertyId: string
  sources: AskSource[]
}

/** Renders one citation row per distinct source cited by an assistant answer. */
export function AskSourceCitations({ propertyId, sources }: AskSourceCitationsProps) {
  const deduped = dedupeSources(sources)
  return (
    <div className="mt-2 space-y-1">
      {deduped.map((source, index) => (
        <SourceCitationRow
          key={`${source.sourceType}-${source.sourceRef}-${source.refId || index}`}
          propertyId={propertyId}
          source={source}
        />
      ))}
    </div>
  )
}
